// Tenant Configuration API.
//
// Backed by Django's tenantapps.configuration app:
//   GET    /api/configuration/         — read singleton
//   PATCH  /api/configuration/         — partial update (tenant admin)
//   PUT    /api/configuration/         — full replace (tenant admin)
//   GET    /api/configuration/choices/ — dropdown catalogs

import { apiClient } from "./apiClient";

export type DateFormat = "MM/DD/YYYY" | "DD/MM/YYYY" | "YYYY-MM-DD" | "DD-MMM-YYYY";

export interface TenantConfiguration {
  id: string;
  timezone: string;
  currency: string;
  date_format: DateFormat;
  language: string;
  data_retention: string;
  notifications_enabled: boolean;
  auto_save_enabled: boolean;
  /** Free-form bucket for module-level toggles and feature flags. */
  extras: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  updated_by_username: string;
}

/** PATCH payload — every field optional. */
export type ConfigurationPatch = Partial<
  Pick<
    TenantConfiguration,
    | "timezone"
    | "currency"
    | "date_format"
    | "language"
    | "data_retention"
    | "notifications_enabled"
    | "auto_save_enabled"
    | "extras"
  >
>;

export interface ConfigurationChoice {
  value: string;
  label: string;
}

export interface ConfigurationChoices {
  timezones: ConfigurationChoice[];
  currencies: ConfigurationChoice[];
  date_formats: ConfigurationChoice[];
  languages: ConfigurationChoice[];
  data_retentions: ConfigurationChoice[];
}

export const configurationApi = {
  get: () => apiClient.get<TenantConfiguration>("/configuration/"),
  patch: (data: ConfigurationPatch) =>
    apiClient.patch<TenantConfiguration>("/configuration/", data),
  put: (data: ConfigurationPatch) =>
    apiClient.put<TenantConfiguration>("/configuration/", data),
  choices: () => apiClient.get<ConfigurationChoices>("/configuration/choices/"),
};

/**
 * Fallback used by the FE provider when /configuration/ hasn't returned
 * yet (or fails). Mirrors the BE defaults so any formatter that runs
 * before the network call has a sensible value to use.
 */
export const DEFAULT_CONFIGURATION: TenantConfiguration = {
  id: "",
  timezone: "Asia/Kolkata",
  currency: "USD",
  date_format: "DD/MM/YYYY",
  language: "en",
  data_retention: "1y",
  notifications_enabled: true,
  auto_save_enabled: true,
  extras: {},
  created_at: "",
  updated_at: "",
  updated_by_username: "",
};
